import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/configureStore';
import { newNotification, Notification } from '../../actions/uiActions';
import { logToDatadog } from '../../services/datadog';
// Removed legacy AddressFind types
import { getCells, setSelectedCell } from '../../actions/orderActions';
import { getGeofences } from '../../actions/geofenceActions';
import { getObstacles } from '../../actions/obstacleActions';
import { selectObstacles } from '../../reducers/obstaclesReducer';
import { blanchCellId } from '../../constants';
import { 
  MapType, 
  MobileDropAnalyzerProps,
  MAP_CONFIG,
} from './types';
import { Obstacle } from '../../models/obstacle.interface';
 

import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap, Pane } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// New: Pull in Drop Point Management hook and layers to render building GeoJSON via places API
import PlaceLayer from '../DropPointManagement/components/PlaceLayer/PlaceLayer';
import DropPointLayer from '../DropPointManagement/components/DropPointLayer/DropPointLayer';
import MapEvents from '../DropPointManagement/components/MapEvents/MapEvents';
import { useDropPointManagement } from '../DropPointManagement/hooks/useDropPointManagement';
import type { ClickMode } from '../DropPointManagement/models/click-mode';
import type { DropPoint, Place } from '@m4a3/api-client';
import { DropPointRow } from '../../components/PlaceContextDropPoints/PlaceContextDropPoints';
import { orderfulApi } from '../../services/api-instances';
import { isVerifiedRiskOnlyCell } from '../../utils/cellDropPointUtils';
import Obstacles from '../Restaurant/components/Obstacles';

// Fix default markers for React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icon for selected location
const selectedMarkerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5S12.5 41 12.5 41S25 19.4 25 12.5S19.4 0 12.5 0z" fill="#2563eb"/>
      <circle cx="12.5" cy="12.5" r="6" fill="white"/>
      <circle cx="12.5" cy="12.5" r="3" fill="#2563eb"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Removed legacy AddressFind marker icons

// Types are now imported from ./types.ts

// Map click handler component
function MapClickHandler({ onMapClick }: { onMapClick: (latlng: L.LatLng) => void }) {
  useMapEvents({
    click: (e: any) => {
      const oe: any = e?.originalEvent;
      if (oe) {
        const path = typeof oe.composedPath === 'function' ? oe.composedPath() : (oe.path || []);
        if (Array.isArray(path)) {
          const isInteractive = path.some((el: any) => {
            try {
              return el?.classList?.contains('leaflet-interactive') || el?.classList?.contains('leaflet-marker-icon') || el?.classList?.contains('leaflet-popup');
            } catch {
              return false;
            }
          });
          if (isInteractive) {
            // Prevent click-through placing a new prospective point when clicking a drop point/building
            oe.preventDefault?.();
            oe.stopPropagation?.();
            return;
          }
        } else {
          const target = oe.target as HTMLElement | undefined;
          if (target && (target.classList?.contains('leaflet-interactive') || target.classList?.contains('leaflet-marker-icon'))) {
            oe.preventDefault?.();
            oe.stopPropagation?.();
            return;
          }
        }
      }
      onMapClick(e.latlng);
    },
  });
  return null;
}

// Component to center map on coordinates
function MapUpdater({ center, zoom }: { center: [number, number], zoom: number }) {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  
  return null;
}

function MobileDropAnalyzer({
  notify,
  getCells,
  cells,
  selectedCellId,
  setSelectedCell,
  geofences,
  getGeofences,
  getObstacles
}: Readonly<MobileDropAnalyzerProps>) {
  // New: Use Drop Point Management to load places from new Places API and manage building GeoJSON
  const { state: dpState, actions: dpActions } = useDropPointManagement();
  const obstacles: Obstacle[] = useSelector(selectObstacles);
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const mapRef = useRef<L.Map>(null);
  const [heightData, setHeightData] = useState<number | null>(null);
  const [columnWidth, setColumnWidth] = useState<number>(3);
  const [manualCoords, setManualCoords] = useState<string>('');
  const [riskEvaluationData, setRiskEvaluationData] = useState<string | null>(null);
  const [isLoadingRiskEvaluation, setIsLoadingRiskEvaluation] = useState<boolean>(false);
  const [riskEvaluationError, setRiskEvaluationError] = useState<string | null>(null);
  const [mapType, setMapType] = useState<'manna' | 'satellite' | 'roadmap'>('manna');
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
  const [isSavingDropPoint, setIsSavingDropPoint] = useState<boolean>(false);
  const [isVerifyingDropPoint, setIsVerifyingDropPoint] = useState<boolean>(false);
  const [isSettingPreferred, setIsSettingPreferred] = useState<boolean>(false);
  const [selectedDropPointRisk, setSelectedDropPointRisk] = useState<any>(null);
  const [isLoadingSelectedDropPointRisk, setIsLoadingSelectedDropPointRisk] = useState<boolean>(false);
  const [eircodeInput, setEircodeInput] = useState<string>('');
  const [isSearchingEircode, setIsSearchingEircode] = useState<boolean>(false);
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);

  // Default map center (Blanchardstown)
  const defaultCenter: [number, number] = [53.39292547693334, -6.395731067422041];
  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultCenter);
  const [mapZoom, setMapZoom] = useState<number>(16);

  // Function to get delivery area radius based on cell ID
  const getDeliveryRadiusForCell = (cellId?: number): number => {
    const activeCellId = cellId || blanchCellId;
    return activeCellId === blanchCellId ? 2.25 : 3;
  };

  // Function to determine risk level based on height data
  const getRiskLevel = (height?: number | null): 'HIGH' | 'LOW' | 'UNKNOWN' => {
    if (height === null || height === undefined || isNaN(height)) {
      return 'UNKNOWN';
    }
    if (height >= 1.0) {
      return 'HIGH';
    } else {
      return 'LOW';
    }
  };

  // Function to get circle colors based on building risk and height data
  const getCircleColors = (height?: number | null): { color: string; fillColor: string; weight: number } => {
    // Show white circle while loading assessment
    if (isLoadingRiskEvaluation) {
      return { color: '#FFFFFF', fillColor: '#FFFFFF', weight: 3 }; // White while loading
    }

    // Border color based on building risk evaluation
    let borderColor = '#888888'; // Default gray
    if (riskEvaluationData === 'HIGH') {
      borderColor = '#DC2626'; // Red border for near building
    } else if (riskEvaluationData === 'LOW') {
      borderColor = '#10B981'; // Green border for clear area
    }

    // Background color based on height data (independent of building risk)
    let fillColor = '#888888'; // Default gray for no data/unknown
    if (height !== null && height !== undefined && !isNaN(height)) {
      if (height >= 1.0) {
        fillColor = '#DC2626'; // Red background for unsafe (obstacles detected ≥ 1.0m)
      } else {
        fillColor = '#10B981'; // Green background for safe (< 1.0m height)
      }
    }

    return { color: borderColor, fillColor: fillColor, weight: 3 };
  };

  // Whether at least one risk is acceptable (building or ground)
  const canCreateVerifiedDropPoint = useMemo(() => {
    if (!markerPosition) return false;
    const buildingOk = riskEvaluationData === 'LOW';
    const groundOk = typeof heightData === 'number' && !isNaN(heightData) && heightData < 1.0;
    return buildingOk || groundOk;
  }, [markerPosition, riskEvaluationData, heightData]);

  const handleCreateVerifiedDropPoint = useCallback(async () => {
    if (!markerPosition) return;
    if (!dpState.selectedPlaceContext || !dpState.selectedPlace) {
      notify({ variant: 'error', message: 'Select a building first' });
      return;
    }
    if (!canCreateVerifiedDropPoint) {
      notify({ variant: 'error', message: 'Location not eligible (no passing risk check)' });
      return;
    }
    try {
      setIsSavingDropPoint(true);
      await orderfulApi.createDropPoint(dpState.selectedPlaceContext.placeId, {
        latitude: markerPosition[0],
        longitude: markerPosition[1],
        verified: true,
      } as any);
      // Refresh place context to include the new drop point
      // Clear caches to force fresh data, then re-select the same place
      if (dpActions.clearCache) {
        await dpActions.clearCache();
      }
      await dpActions.handlePlaceClick(dpState.selectedPlace);
      notify({ variant: 'success', message: 'Verified drop point created' });
      // Optionally clear prospecting marker after creation
      setMarkerPosition(null);
      setHeightData(null);
      setRiskEvaluationData(null);
      setRiskEvaluationError(null);
    } catch (err) {
      console.error('Failed to create drop point', err);
      notify({ variant: 'error', message: 'Failed to create drop point' });
    } finally {
      setIsSavingDropPoint(false);
    }
  }, [markerPosition, dpState.selectedPlaceContext, dpState.selectedPlace, canCreateVerifiedDropPoint, dpActions, notify]);

  // Function to fetch building risk evaluation and ground height using Orderful API
  const evaluateRiskWithOrderful = async (latitude: number, longitude: number) => {
    const cellIdForEval = selectedCellId || 7;
    const risk = await orderfulApi.evaluateDropPointRisk({
      latitude,
      longitude,
      radius: 3.0,
      cellId: cellIdForEval,
    });
    return risk;
  };

  // Load risk evaluation and height (from Orderful) when marker position changes
  useEffect(() => {
    if (markerPosition && markerPosition[0] && markerPosition[1]) {
      setIsLoadingRiskEvaluation(true);
      setRiskEvaluationError(null);
      evaluateRiskWithOrderful(markerPosition[0], markerPosition[1])
        .then((risk) => {
          setRiskEvaluationData(risk.isNearBuilding ? 'HIGH' : 'LOW');
          setHeightData(typeof risk.heightData?.height === 'number' ? risk.heightData.height : null);
        })
        .catch(error => {
          console.error('Error fetching risk data:', error);
          setRiskEvaluationError(error?.message || 'Failed to evaluate risk');
          notify({ variant: 'error', message: 'Failed to load risk data' });
        })
        .finally(() => {
          setIsLoadingRiskEvaluation(false);
        });
    }
  }, [markerPosition, selectedCellId]);

  // Update delivery radius when cell ID changes
  useEffect(() => {
    const cellId = selectedCellId || null;
    if (cellId) {
      const newRadius = getDeliveryRadiusForCell(cellId);
      setColumnWidth(newRadius);
    }
  }, [selectedCellId]);

  // Load basic data on mount
  useEffect(() => {
    getCells();
    getGeofences();
  }, []);

  // Load obstacles when the selected cell changes
  useEffect(() => {
    if (selectedCellId && typeof getObstacles === 'function') {
      getObstacles(selectedCellId);
    }
  }, [selectedCellId, getObstacles]);

  // Center map on selected cell when it changes
  useEffect(() => {
    if (!cells || !Array.isArray(cells)) return;
    const selectedCell = cells.find((c: any) => c?.id === selectedCellId);
    const lat = selectedCell?.centerLatitude;
    const lng = selectedCell?.centerLongitude;
    if (typeof lat === 'number' && typeof lng === 'number') {
      const offsetLat = lat + (MAP_CONFIG.PIN_VISIBILITY_OFFSET as number);
      setMapCenter([offsetLat, lng]);
      setMapZoom(MAP_CONFIG.ZOOM_LEVELS.DETAIL as number);
    }
  }, [selectedCellId, cells]);

  // Handle map click
  const handleMapClick = useCallback((latlng: L.LatLng) => {
    // If a drop point is currently selected, deselect it (like building selection) when starting a new prospective point
    if (dpState.selectedDropPointId && dpState.selectedPlace) {
      // Re-select the current place to clear drop point selection without changing place
      dpActions.handlePlaceClick(dpState.selectedPlace);
    }
    // Clear any previously computed selected DP risk
    setSelectedDropPointRisk(null);

    setMarkerPosition([latlng.lat, latlng.lng]);
    setManualCoords(`${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`);
  }, [dpActions, dpState.selectedDropPointId, dpState.selectedPlace]);

  // Handle manual coordinate input
  const handleManualCoordinates = () => {
    const coords = manualCoords.trim().split(',');
    if (coords.length !== 2) {
      notify({
        variant: 'error',
        message: 'Please enter coordinates in format: latitude, longitude'
      });
      return;
    }
    
    const lat = parseFloat(coords[0].trim());
    const lng = parseFloat(coords[1].trim());
    
    if (isNaN(lat) || isNaN(lng)) {
      notify({
        variant: 'error',
        message: 'Please enter valid latitude and longitude values'
      });
      return;
    }
    
    if (lat < -90 || lat > 90) {
      notify({
        variant: 'error',
        message: 'Latitude must be between -90 and 90'
      });
      return;
    }
    
    if (lng < -180 || lng > 180) {
      notify({
        variant: 'error',
        message: 'Longitude must be between -180 and 180'
      });
      return;
    }
    
    setHeightData(null);
    setRiskEvaluationData(null);
    setRiskEvaluationError(null);
    
    const newRadius = getDeliveryRadiusForCell(selectedCellId);
    setColumnWidth(newRadius);
    
    setMarkerPosition([lat, lng]);
    
    // Apply offset to ensure pin is visible above bottom UI panel  
    // Small offset to position pin comfortably above bottom UI panel
    const offsetLat = lat - 0.0005; // Approximately 55 meters south offset
    setMapCenter([offsetLat, lng]);
    setMapZoom(17); // Consistent zoom level with pin selection
    
    notify({
      variant: 'success',
      message: `Map updated to coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)} (Cell ${selectedCellId})`
    });
  };

  // Handle drop point click from Place context
  const onPlaceContextDropPointClick = useCallback((dropPoint: DropPoint) => {
    dpActions.handleDropPointClick(dropPoint);
    // Clear any prospective marker and risk state when selecting an existing drop point
    setMarkerPosition(null);
    setHeightData(null);
    setRiskEvaluationData(null);
    setRiskEvaluationError(null);
    setSelectedDropPointRisk(null);

    // Evaluate risk for the selected drop point
    setIsLoadingSelectedDropPointRisk(true);
    const cellIdForEval = selectedCellId || 7;
    orderfulApi.evaluateDropPointRisk({
      latitude: dropPoint.latitude,
      longitude: dropPoint.longitude,
      radius: 3.0,
      cellId: cellIdForEval,
    })
      .then((risk) => {
        const assessment: any = {
          buildingRisk: risk.isNearBuilding ? 'HIGH' : 'LOW',
          groundRisk: typeof risk.heightData?.height === 'number' ? (risk.heightData.height >= 1.0 ? 'HIGH' : 'LOW') : 'UNKNOWN',
          overallRisk: 'UNKNOWN',
          canSave: false,
          message: '',
          heightData: typeof risk.heightData?.height === 'number' ? risk.heightData.height : undefined,
        };
        setSelectedDropPointRisk(assessment);
      })
      .catch((err) => {
        console.error('Drop point risk eval failed', err);
        setSelectedDropPointRisk({ buildingRisk: 'ERROR', groundRisk: 'ERROR', overallRisk: 'ERROR', canSave: false, message: 'Risk eval failed', hasError: true } as any);
      })
      .finally(() => setIsLoadingSelectedDropPointRisk(false));

    const lat = dropPoint.latitude;
    const lng = dropPoint.longitude;
    if (mapRef.current) {
      const map = mapRef.current;
      const bounds = map.getBounds();
      const pinLatLng = L.latLng(lat, lng);
      const isInBounds = bounds.contains(pinLatLng);
      const currentCenter = map.getCenter();
      const mapBounds = map.getBounds();
      const mapHeight = mapBounds.getNorth() - mapBounds.getSouth();
      const bottomThreshold = currentCenter.lat - (mapHeight * 0.25);
      const isPinTooLow = lat < bottomThreshold;

      if (!isInBounds || isPinTooLow) {
        const offsetLat = lat - 0.0005;
        setMapCenter([offsetLat, lng]);
        setMapZoom(17);
        map.setView([offsetLat, lng], 17);
      }
    }
  }, [dpActions, selectedCellId]);

  // When a place is selected (building click), clear any prospecting marker
  const onPlaceSelected = useCallback((place: Place) => {
    dpActions.handlePlaceClick(place);
    setMarkerPosition(null);
    setHeightData(null);
    setRiskEvaluationData(null);
    setRiskEvaluationError(null);
    setSelectedDropPointRisk(null);
  }, [dpActions]);

  // Search for a place by Eircode and select it
  const handleSearchByEircode = useCallback(async () => {
    const raw = eircodeInput.trim();
    if (!raw) {
      notify({ variant: 'error', message: 'Please enter an Eircode' });
      return;
    }
    try {
      setIsSearchingEircode(true);
      const eircode = raw.replace(/\s+/g, '').toUpperCase();
      const context = await orderfulApi.getPlaceContextByEircode(eircode);

      // Clear any existing prospecting marker and risk state
      setMarkerPosition(null);
      setHeightData(null);
      setRiskEvaluationData(null);
      setRiskEvaluationError(null);
      setSelectedDropPointRisk(null);

      // Center map on the place centroid with a slight offset and trigger a places reload
      const lat = context?.place?.centroid?.latitude;
      const lng = context?.place?.centroid?.longitude;
      if (typeof lat === 'number' && typeof lng === 'number') {
        const offsetLat = lat - 0.0005;
        setMapCenter([offsetLat, lng]);

        // Explicitly trigger places reload for this viewport
        dpActions.handleMapMove(L.latLng(offsetLat, lng), mapZoom);
      }

      // Use existing handler to select the place and load its context (after map move trigger)
      await dpActions.handlePlaceClick(context.place);
      notify({ variant: 'success', message: `Selected building for ${eircode}` });
    } catch (err: any) {
      console.error('Eircode search failed', err);
      notify({ variant: 'error', message: err?.message || 'Failed to find building for Eircode' });
    } finally {
      setIsSearchingEircode(false);
    }
  }, [eircodeInput, dpActions, notify]);

  


  // Copy coordinates to clipboard
  const copyCoordinatesToClipboard = () => {
    if (markerPosition) {
      const coordsText = `${markerPosition[0].toFixed(6)}, ${markerPosition[1].toFixed(6)}`;
      navigator.clipboard.writeText(coordsText).then(() => {
        notify({
          variant: 'success',
          message: 'Coordinates copied to clipboard'
        });
      }).catch(() => {
        notify({
          variant: 'error',
          message: 'Failed to copy coordinates'
        });
      });
    }
  };

  // Get user's current location and center map
  const centerOnCurrentLocation = () => {
    if (!navigator.geolocation) {
      notify({
        variant: 'error',
        message: 'Geolocation is not supported by this browser'
      });
      return;
    }

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Clear previous data
        setHeightData(null);
        setRiskEvaluationData(null);
        setRiskEvaluationError(null);
        
        // Set new position
        setMarkerPosition([latitude, longitude]);
        
        // Apply offset to ensure current location pin is visible above bottom UI panel
        // Small offset to position pin comfortably above bottom UI panel
        const offsetLat = latitude - 0.0005; // Approximately 55 meters south offset
        setMapCenter([offsetLat, longitude]);
        setMapZoom(17); // Zoom in when centering on user location
        setManualCoords(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        
        notify({
          variant: 'success',
          message: `Centered on your location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
        });
        
        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);
        
        let errorMessage = 'Failed to get your location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'An unknown error occurred while getting location.';
            break;
        }
        
        notify({
          variant: 'error',
          message: errorMessage
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000, // 10 seconds
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Determine if marker is at the selected drop point from place context
  const isMarkerAtSelectedDropPoint = useMemo(() => {
    if (!markerPosition || !dpState.selectedDropPointId) return false;
    const selected = dpState.dropPoints.find(dp => dp.id === dpState.selectedDropPointId);
    if (!selected) return false;
    return Math.abs(selected.latitude - markerPosition[0]) < 1e-6 && Math.abs(selected.longitude - markerPosition[1]) < 1e-6;
  }, [markerPosition, dpState.selectedDropPointId, dpState.dropPoints]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b p-4 flex-shrink-0">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Mobile Drop Analyzer</h1>
            <p className="text-sm text-gray-600 mt-1">Tap map to analyze drop points</p>
          </div>
          <div className="text-right">
            <div className="flex items-start justify-end space-x-2">
              <div className="text-right">
                <div className="text-xs text-gray-500 mb-1">Cell</div>
                <select
                  value={selectedCellId}
                  onChange={(e) => setSelectedCell && setSelectedCell(Number(e.target.value))}
                  className={`inline-flex items-center px-3 py-2 rounded-full text-xs font-medium transition-all bg-indigo-100 text-indigo-800 hover:bg-indigo-200`}
                  title="Select cell"
                >
                  {(cells || []).map((c: any) => (
                    <option key={`cell-${c.id}`} value={c.id}>{c.cellName || `Cell ${c.id}`}</option>
                  ))}
                </select>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 mb-1">Tap to change</div>
                <button
                  onClick={() => {
                    const mapTypes: ('manna' | 'satellite' | 'roadmap')[] = ['manna', 'satellite', 'roadmap'];
                    const currentIndex = mapTypes.indexOf(mapType);
                    const nextIndex = (currentIndex + 1) % mapTypes.length;
                    setMapType(mapTypes[nextIndex]);
                  }}
                  className={`inline-flex items-center px-3 py-2 rounded-full text-xs font-medium transition-all hover:scale-105 active:scale-95 ${
                    mapType === 'manna' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' :
                    mapType === 'satellite' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                    'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                  title="Tap to cycle through map views"
                >
                  {mapType === 'manna' && '📡 Manna'}
                  {mapType === 'satellite' && '🛰️ Satellite'}
                  {mapType === 'roadmap' && '🗺️ Roadmap'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%', zIndex: 1, position: 'relative' }}
          maxZoom={23}
          zoomControl={false}
          ref={mapRef}
        >
          <MapUpdater center={mapCenter} zoom={mapZoom} />
          <MapClickHandler onMapClick={handleMapClick} />
          {/* New: Wire map move/zoom to useDropPointManagement so Places API loads and we render building GeoJSON */}
          <MapEvents
            onMapClick={(coordinates) => { /* clicks handled by MapClickHandler above */ }}
            onMapMove={dpActions.handleMapMove}
            onZoom={dpActions.handleMapZoom}
            clickMode={'select' as ClickMode}
          />
          
          {/* Conditional Tile Layers based on map type */}
          {mapType === 'manna' && (
            <>
              {/* Manna Tiles */}
              <TileLayer
                attribution="Manna"
                url="https://tiles.manna.aero/{z}/{x}/{y}.png"
                maxZoom={24}
              />
              {/* Background OSM Tiles for context */}
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                opacity={0.3}
              />
            </>
          )}
          
          {mapType === 'satellite' && (
            <TileLayer
              attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          )}
          
          {mapType === 'roadmap' && (
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          )}
          
          {/* New: Buildings GeoJSON from Places API */}
          <PlaceLayer
            places={dpState.places}
            selectedPlaceId={dpState.selectedPlaceId}
            placeStatuses={dpState.placeStatuses}
            placesUpdateKey={dpState.placesUpdateKey}
            clickMode={dpState.clickMode}
            onPlaceClick={onPlaceSelected}
            enablePopup={false}
            disableTooltip={true}
          />

          {/* Obstacles */}
          <Pane name="Obstacles" style={{ zIndex: 10005 }}>
            <Obstacles obstacles={obstacles as any} />
          </Pane>

          {/* Selected Position Marker */}
          {markerPosition && (
            <Marker position={markerPosition} icon={selectedMarkerIcon}>
              <Popup>
                <div className="text-sm">
                  <p><strong>Selected Position</strong></p>
                  <p>Lat: {markerPosition[0].toFixed(6)}</p>
                  <p>Lng: {markerPosition[1].toFixed(6)}</p>
                  {heightData !== null && (
                    <p>Height: {heightData.toFixed(1)}m AGL</p>
                  )}
                  {riskEvaluationData && (
                    <p>Building Risk: {riskEvaluationData}</p>
                  )}
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Safety Analysis Circle */}
          {markerPosition && (
            <Circle
              center={markerPosition}
              radius={columnWidth}
              pathOptions={{
                ...getCircleColors(heightData),
                fillOpacity: 0.2,
                opacity: 0.8
              }}
            >
              <Popup>
                <div className="text-sm">
                  <p><strong>Safety Analysis Circle</strong></p>
                  <p>Radius: {columnWidth}m (delivery area)</p>
                  <p>Ground Risk: {getRiskLevel(heightData)}</p>
                  <p>Building Risk: {riskEvaluationData || 'Loading...'}</p>
                  {heightData !== null && (
                    <p>Height: {heightData.toFixed(1)}m AGL</p>
                  )}
                  <div className="mt-2 text-xs text-gray-600">
                    <p>🔴 Red border: Near building</p>
                    <p>🟢 Green border: Clear area</p>
                    <p>🔴 Red fill: Obstacles ≥1.0m</p>
                    <p>🟢 Green fill: Safe ground (&lt;1.0m)</p>
                  </div>
                </div>
              </Popup>
            </Circle>
          )}
          
          {/* Drop Points from Place Context */}
          <DropPointLayer
            dropPoints={dpState.dropPoints}
            selectedDropPointId={dpState.selectedDropPointId}
            preferredDropPointId={dpState.selectedPlaceContext?.place?.preferredDropPointId || null}
            onDropPointClick={onPlaceContextDropPointClick}
          />
        </MapContainer>

        {/* Zoom Controls */}
        <div className="absolute top-4 right-4 z-[9999] flex flex-col space-y-2">
          <button
            onClick={() => setMapZoom(Math.min(mapZoom + 1, 23))}
            className="bg-white shadow-lg rounded-lg p-3 text-gray-700 hover:bg-gray-50 border border-gray-200"
            title="Zoom in"
            style={{ zIndex: 9999 }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            onClick={() => setMapZoom(Math.max(mapZoom - 1, 8))}
            className="bg-white shadow-lg rounded-lg p-3 text-gray-700 hover:bg-gray-50 border border-gray-200"
            title="Zoom out"
            style={{ zIndex: 9999 }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <button
            onClick={centerOnCurrentLocation}
            disabled={isGettingLocation}
            className={`bg-white shadow-lg rounded-lg p-3 transition-all border border-gray-200 ${
              isGettingLocation 
                ? 'text-blue-600 cursor-not-allowed' 
                : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
            }`}
            title="Center on my location"
            style={{ zIndex: 9999 }}
          >
            {isGettingLocation ? (
              <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>

        {/* Removed Drop History Button */}
      </div>

      {/* Bottom Panel */}
      <div className="bg-white border-t shadow-lg flex-shrink-0">
        {/* Selected Place summary (from place context) */}
        {dpState.selectedPlaceContext && (
          <div className="p-4 border-b">
            <div className="text-sm font-semibold text-gray-900">
              {dpState.selectedPlaceContext.place.friendlyName || 'Selected Building'}
            </div>
            {dpState.selectedPlaceContext.place.address && (
              <div className="text-xs text-gray-700 mt-0.5">
                {dpState.selectedPlaceContext.place.address}
              </div>
            )}
            <div className="text-xs text-gray-600 mt-1">
              Status: {dpState.selectedPlaceContext.place.enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        )}
        {/* Selected Drop Point Details */}
        {dpState.selectedDropPointId && dpState.selectedPlaceContext && (
          <div className="p-4 border-b">
            {(() => {
              const selected = dpState.dropPoints.find(dp => dp.id === dpState.selectedDropPointId);
              if (!selected) return null;
              return (
                <>
                  <DropPointRow
                    dropPoint={selected}
                    placeContext={dpState.selectedPlaceContext}
                    isSelected={true}
                    onSelect={(id) => {
                      const dp = dpState.dropPoints.find(d => d.id === id);
                      if (dp) onPlaceContextDropPointClick(dp);
                    }}
                    riskAssessment={selectedDropPointRisk}
                    isLoadingRiskAssessment={isLoadingSelectedDropPointRisk}
                    selectedDropPointId={dpState.selectedDropPointId}
                    isNarrow={true}
                    showManagementFeatures={false}
                  />
                  {!selected.verified && (
                    <div className="mt-3">
                      <button
                        onClick={async () => {
                          if (!dpState.selectedPlaceContext) return;
                          try {
                            setIsVerifyingDropPoint(true);
                            await orderfulApi.updateDropPointById(
                              selected.id,
                              { verified: true } as any
                            );
                            if (dpActions.clearCache) {
                              await dpActions.clearCache();
                            }
                            await dpActions.handlePlaceClick(dpState.selectedPlace!);
                            notify({ variant: 'success', message: 'Drop point verified' });
                          } catch (e) {
                            console.error('Failed to verify drop point', e);
                            notify({ variant: 'error', message: 'Failed to verify drop point' });
                          } finally {
                            setIsVerifyingDropPoint(false);
                          }
                        }}
                        disabled={isVerifyingDropPoint}
                        className={`w-full px-4 py-2 rounded-lg text-sm font-medium ${
                          isVerifyingDropPoint ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {isVerifyingDropPoint ? 'Verifying…' : 'Verify Drop Point'}
                      </button>
                    </div>
                  )}
                  <div className="mt-3">
                    {(() => {
                      const preferredId = (dpState.selectedPlaceContext.place as any)?.preferredDropPointId || null;
                      const alreadyPreferred = preferredId === selected.id;
                      const restrictedUnverified = isVerifiedRiskOnlyCell(Number(selectedCellId || 0)) && !selected.verified;
                      const disabled = isSettingPreferred || alreadyPreferred || restrictedUnverified;
                      const title = restrictedUnverified ? 'Verify delivery spot before setting as preferred' : undefined;
                      return (
                        <button
                          onClick={async () => {
                            if (!dpState.selectedPlaceContext || alreadyPreferred) return;
                            try {
                              setIsSettingPreferred(true);
                              await orderfulApi.updatePlaceContext(dpState.selectedPlaceContext.placeId, {
                                enabled: dpState.selectedPlaceContext.place.enabled,
                                preferredDropPointId: selected.id,
                              } as any);
                              if (dpActions.clearCache) {
                                await dpActions.clearCache();
                              }
                              await dpActions.handlePlaceClick(dpState.selectedPlace!);
                              notify({ variant: 'success', message: 'Preferred drop point updated' });
                            } catch (e) {
                              console.error('Failed to set preferred delivery spot', e);
                              notify({ variant: 'error', message: 'Failed to set preferred delivery spot' });
                            } finally {
                              setIsSettingPreferred(false);
                            }
                          }}
                          disabled={disabled}
                          title={title}
                          className={`w-full px-4 py-2 rounded-lg text-sm font-medium ${
                            disabled ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                        >
                          {alreadyPreferred ? 'Preferred' : (isSettingPreferred ? 'Setting…' : 'Set as Preferred')}
                        </button>
                      );
                    })()}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Search (collapsible) */}
        <div className="border-b">
          <button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="w-full flex items-center justify-between p-4"
            aria-expanded={isSearchOpen}
            aria-controls="search-section"
          >
            <span className="text-sm font-semibold text-gray-900">Search</span>
            <svg className={`w-5 h-5 text-gray-600 transform transition-transform ${isSearchOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isSearchOpen && (
            <div id="search-section" className="px-4 pb-4">
              <div className="space-y-4">
                {/* Eircode Search */}
                <div>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      placeholder="Eircode (e.g., D15 ABC1)"
                      value={eircodeInput}
                      onChange={(e) => setEircodeInput(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      onClick={handleSearchByEircode}
                      disabled={!eircodeInput.trim() || isSearchingEircode}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        !eircodeInput.trim() || isSearchingEircode
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {isSearchingEircode ? 'Searching…' : 'Search'}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">Search building by Eircode</div>
                </div>

                {/* Coordinate Input */}
                <div>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      placeholder="53.397, -6.396"
                      value={manualCoords}
                      onChange={(e) => setManualCoords(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleManualCoordinates}
                      disabled={!manualCoords.trim()}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:bg-gray-300"
                    >
                      Go to Coordinates
                    </button>
                    {markerPosition && (
                      <button
                        onClick={copyCoordinatesToClipboard}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Safety Status */}
        {markerPosition && (
          <div className="p-4 border-b">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Building Risk</p>
                {isLoadingRiskEvaluation ? (
                  <p className="text-sm text-blue-600">Loading...</p>
                ) : riskEvaluationError ? (
                  <p className="text-sm text-red-600">Error</p>
                ) : riskEvaluationData ? (
                  <p className={`text-sm font-medium ${
                    riskEvaluationData === 'HIGH' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {riskEvaluationData === 'HIGH' ? 'HIGH (Near building)' : 'LOW (Clear area)'}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">Unknown</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Ground Risk</p>
                {heightData !== null ? (
                  <p className={`text-sm font-medium ${
                    getRiskLevel(heightData) === 'HIGH' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {getRiskLevel(heightData) === 'HIGH' ? 'UNSAFE' : 'SAFE'} ({heightData.toFixed(1)}m)
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">Unknown</p>
                )}
              </div>
            </div>
            <div className="mt-3">
              <button
                onClick={handleCreateVerifiedDropPoint}
                disabled={!canCreateVerifiedDropPoint || isSavingDropPoint || !dpState.selectedPlace}
                className={`w-full px-4 py-2 rounded-lg text-sm font-medium ${
                  !canCreateVerifiedDropPoint || isSavingDropPoint || !dpState.selectedPlace
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isSavingDropPoint ? 'Creating…' : 'Create Drop Point'}
              </button>
              {!dpState.selectedPlace && (
                <p className="mt-1 text-xs text-gray-500">Select a building first</p>
              )}
              {!canCreateVerifiedDropPoint && dpState.selectedPlace && (
                <p className="mt-1 text-xs text-orange-600">Requires at least one passing check (building or ground)</p>
              )}
            </div>
          </div>
        )}

        {/* Removed validation button and flow */}
      </div>
      {/* Removed Drop Location History Modal (AddressFind) */}
      {/* Removed validation confirmation dialog */}
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  geofences: state.geofences.geofences,
  obstacles: state.obstacles.list,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  notify: (notification: Notification) => dispatch(newNotification(notification)),
  getCells: () => dispatch(getCells()),
  getGeofences: () => dispatch(getGeofences()),
  getObstacles: (cellId: number) => dispatch(getObstacles(cellId)),
  setSelectedCell: (cellId: number) => dispatch(setSelectedCell(cellId, true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MobileDropAnalyzer); 